import { toolLabel } from '@/features/editor-access/knownTools';
import type { UserSummary } from '@/lib/api/generated/model';

/**
 * The sentences spec 144 puts a name and a time into, in one place.
 *
 * The header, the sidebar's marker, the Workflows list and the discard
 * confirmation all say who saved a draft and when. Written once here, they
 * cannot drift into four ways of saying it — and the tests read the same
 * functions, so a test written in London does not fail in a browser set to
 * another clock.
 */

const CLOCK = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
});

const DAY_AND_CLOCK = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * When a draft was saved: the clock time today, the day as well otherwise.
 *
 * A bare "14:32" from last week would read as this afternoon — the one
 * misreading that makes an abandoned draft look like someone's work in
 * progress.
 */
export function formatSavedAt(at: string, now: Date = new Date()): string {
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return '';
  return date.toDateString() === now.toDateString()
    ? CLOCK.format(date)
    : DAY_AND_CLOCK.format(date);
}

/** Who saved. "someone" when the service no longer knows the person. */
export function saverName(savedBy: UserSummary | null | undefined): string {
  return savedBy?.display_name ?? 'someone';
}

/**
 * The header's line for a published workflow with a saved draft (FR-005), and
 * the AI tool it was saved through when it was one (spec 246).
 */
export function draftStatusLine(
  savedBy: UserSummary | null,
  savedAt: string,
  savedVia: string | null = null,
): string {
  const via = savedVia ? ` via ${toolLabel(savedVia)}` : '';
  return `Draft · not live yet · saved by ${saverName(savedBy)}${via}, ${formatSavedAt(savedAt)}`;
}

/** The Draft marker's tooltip in the sidebar and the Workflows list (FR-010). */
export function draftMarkerTooltip(
  savedBy: UserSummary | null | undefined,
  savedAt: string | null | undefined,
): string {
  return `${PENDING_CHANGES_LABEL}, ${pendingChangesSaved(savedBy, savedAt)}`;
}

/** What a published workflow with unpublished changes is marked with on the
 *  Workflows page (spec 175). Not "Draft": that word is a workflow never
 *  published, and the page's own count of drafts did not include these. */
export const PENDING_CHANGES_LABEL = 'Unpublished changes';

/** Who saved the unpublished changes and when, e.g. "saved by Sam, 8 Oct, 14:32". */
export function pendingChangesSaved(
  savedBy: UserSummary | null | undefined,
  savedAt: string | null | undefined,
): string {
  const by = `saved by ${saverName(savedBy)}`;
  return savedAt ? `${by}, ${formatSavedAt(savedAt)}` : by;
}

/** The discard confirmation's question (FR-006). */
export function discardDraftQuestion(
  savedBy: UserSummary | null,
  savedAt: string,
): string {
  return `Discard the draft ${saverName(savedBy)} saved at ${formatSavedAt(savedAt)}?`;
}

/** The browser backup's banner (FR-009). */
export function backupBannerText(savedAt: string): string {
  return `You have unsaved changes from ${formatSavedAt(savedAt)} on this browser`;
}

/**
 * An out-of-date draft (FR-011): said when one is opened, before anyone tries
 * to publish it, and again if a publish is refused for it.
 */
export const OUTDATED_DRAFT_MESSAGE =
  'This draft is out of date: the live workflow changed since it was started.';
